import { Block } from "app/shared/components/blocks/block";
import EmptyBox from "app/shared/components/empty-box/empty-box";
import { Constant } from "app/shared/constant/constant";
import { STATEMENTDETAIL } from "app/shared/services/services-model.type";
import { spacing } from "app/shared/styles/spacing";
import { formatCurrency } from "app/shared/util/format-currency";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import { nonAccentVietnamese } from "app/shared/util/validate";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { FlatList, ListRenderItem, StyleSheet } from "react-native";
import { Paragraph, TouchableRipple, useTheme } from "react-native-paper";
import DataTableView from "../../../../../shared/components/data-table/data-table.web";
import { FormFilterStatement } from "../../reducer/types";
import StatementTableSearch from "./statement-table-search";

export interface DataStatementV2TableProps {
  ACCFULLNAME?: string;
  ACCNO?: string;
  ACCTYPE?: string;
  BEGINBALANCE?: string;
  BEGINTRANSBALANCE?: string;
  BRNCODE?: string;
  CHANEL?: string;
  CIFNUMBER?: string;
  CONTENT?: string;
  CREATEAMOUNT?: string;
  CURRENBALANCE?: string;
  CURRENCYCODE?: string;
  DEBITAMOUNT?: string;
  ENDBALANCE?: string;
  ENDBATRANSLANCE?: string;
  FROMDATE?: string;
  PRODUCTCODE?: string;
  RQTSEQ?: string;
  SEQUENNO?: string;
  STT?: string;
  T24DATE?: string;
  TODATE?: string;
  TRANSDATE?: string;
  id?: string;
  CURRENCY_CREATE_AMOUNT?: string;
  CURRENCY_DEBIT_AMOUNT?: string;
  FORMAT_TRANS_DATE?: string;
  AMOUNT?: string;
}

interface StatementTableProps {
  onChangeData: (data: STATEMENTDETAIL[]) => void;
  data?: DataStatementV2TableProps[];
  isShow?: boolean;
  onPressRow: (item: any) => void;
  dataFilter?: FormFilterStatement;
  downloadExcel: (values?: FormFilterStatement) => void;
  downloadPdf: (values?: FormFilterStatement) => void;
}

function StatementTable(props: StatementTableProps) {
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();
  const isDesktop = useDesktopMediaQuery();
  const { data = [], onChangeData, isShow = false, onPressRow, dataFilter, downloadExcel, downloadPdf } = props;
  const [dataV2Table, setDataV2Table] = React.useState<DataStatementV2TableProps[]>([]);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        displayShow: { display: isShow ? "flex" : "none" },
        textBold: { ...fonts.medium, color: colors.text, paddingVertical: 0 },
        text: { ...fonts.regular, color: colors.text, paddingVertical: 0 },
        label: { flex: 1, minWidth: "30%" },
        value: { textAlign: "right" },
      }),
    [colors, fonts, isShow],
  );

  const onChangeTextInput = React.useCallback(
    (keyword: string) => {
      if (data && data.length > 0) {
        const filteredData = data.filter((obj: DataStatementV2TableProps) => {
          if (obj) {
            const content = obj?.CONTENT?.length ? nonAccentVietnamese(obj.CONTENT.toLowerCase()) : "";
            const debitAmount = obj?.DEBITAMOUNT?.length ? obj.DEBITAMOUNT.replace(/,/g, "") : "";
            const debitAmount2 = obj?.CURRENCY_DEBIT_AMOUNT?.length ? obj.CURRENCY_DEBIT_AMOUNT : "";
            const creditAmount = obj?.CREATEAMOUNT?.length ? obj.CREATEAMOUNT.replace(/,/g, "") : "";
            const creditAmount2 = obj?.CURRENCY_CREATE_AMOUNT?.length ? obj.CURRENCY_CREATE_AMOUNT : "";
            const date = obj?.TRANSDATE?.length ? moment(obj.TRANSDATE, "YYYYMMDD").format("DDMMYYYY") : "";
            const date2 = obj?.FORMAT_TRANS_DATE?.length ? obj.FORMAT_TRANS_DATE : "";
            return (
              (content.length > 0 && nonAccentVietnamese(content.toLowerCase()).indexOf(nonAccentVietnamese(keyword.toLowerCase())) >= 0) ||
              (debitAmount.length > 0 && debitAmount !== "0" && debitAmount.indexOf(nonAccentVietnamese(keyword.toLowerCase())) >= 0) ||
              (debitAmount2.length > 0 && debitAmount2 !== "0" && debitAmount2.indexOf(nonAccentVietnamese(keyword.toLowerCase())) >= 0) ||
              (creditAmount.length > 0 && creditAmount !== "0" && creditAmount.indexOf(nonAccentVietnamese(keyword.toLowerCase())) >= 0) ||
              (creditAmount2.length > 0 &&
                creditAmount2 !== "0" &&
                creditAmount2.indexOf(nonAccentVietnamese(keyword.toLowerCase())) >= 0) ||
              (date.length > 0 && date.indexOf(nonAccentVietnamese(keyword.toLowerCase())) >= 0) ||
              (date2.length > 0 && date2.indexOf(nonAccentVietnamese(keyword.toLowerCase())) >= 0)
            );
          }
          return obj;
        });
        if (keyword.length === 0) {
          setDataV2Table(data);
        } else {
          setDataV2Table(filteredData);
        }
      }
    },
    [data],
  );

  const onPressRowTable = React.useCallback(
    (item: DataStatementV2TableProps) => {
      onPressRow(item);
    },
    [onPressRow],
  );

  React.useEffect(() => {
    onChangeData(dataV2Table);
  }, [dataV2Table, onChangeData]);

  React.useEffect(() => {
    setDataV2Table(data || []);
  }, [data]);

  const renderItemDetail: ListRenderItem<DataStatementV2TableProps> = ({ item, index }) => (
    <React.Fragment>
      <TouchableRipple key={`${item.id}` + index} onPress={() => onPressRowTable(item)}>
        <Block pv={spacing.small} ph={spacing.medium} bg={index % 2 === 0 ? colors.surface : colors.backgroundForm} br={8}>
          <Block row spaced>
            <Paragraph style={[styles.textBold, styles.label]}>{t("ACC_NGAY_GIAO_DICH")}</Paragraph>
            <Paragraph style={[styles.textBold, styles.value]}>{item.FORMAT_TRANS_DATE}</Paragraph>
          </Block>
          <Block row spaced>
            <Paragraph style={[styles.text, styles.label]}>{t("ACC_SO_THAM_CHIEU")}</Paragraph>
            <Paragraph style={[styles.text, styles.value]}>{item.SEQUENNO}</Paragraph>
          </Block>
          <Block row spaced>
            <Paragraph style={[styles.text, styles.label]}>{t("ACC_SO_TIEN_GHI_NO")}</Paragraph>
            <Paragraph style={[styles.text, styles.value]}>
              {(item?.DEBITAMOUNT?.length && item?.DEBITAMOUNT !== "0" ? "- " : "") + formatCurrency(item.DEBITAMOUNT)}
            </Paragraph>
          </Block>
          <Block row spaced>
            <Paragraph style={[styles.text, styles.label]}>{t("ACC_SO_TIEN_GHI_CO")}</Paragraph>
            <Paragraph style={[styles.text, styles.value]}>
              {(item?.CREATEAMOUNT?.length && item?.CREATEAMOUNT !== "0" ? "+ " : "") + formatCurrency(item.CREATEAMOUNT)}
            </Paragraph>
          </Block>
          <Block row spaced>
            <Paragraph style={[styles.text, styles.label]}>{t("ACC_MO_TA")}</Paragraph>
            <Paragraph style={[styles.text, styles.value]}>{item?.CONTENT}</Paragraph>
          </Block>
        </Block>
      </TouchableRipple>
    </React.Fragment>
  );

  const renderMobile = (
    <FlatList data={dataV2Table} keyExtractor={(_item) => String(_item.id)} renderItem={renderItemDetail} initialNumToRender={10} />
  );

  return (
    <Block style={styles.displayShow}>
      <StatementTableSearch
        onChangeTextInput={onChangeTextInput}
        isShow={data?.length ? true : false}
        downloadExcel={() => downloadExcel(dataFilter)}
        downloadPdf={() => downloadPdf(dataFilter)}
      />
      {isDesktop ? (
        <DataTableView
          header={[
            {
              name: "#",
              attr: "id",
            },
            {
              name: t("ACC_NGAY_GIAO_DICH"),
              attr: "FORMAT_TRANS_DATE",
            },
            {
              name: t("ACC_SO_THAM_CHIEU"),
              attr: "SEQUENNO",
            },
            {
              name: t("ACC_SO_TIEN_GHI_NO"),
              attr: "CURRENCY_DEBIT_AMOUNT",
              isCustomDataColumn: true,
              type: Constant.DEBIT,
            },
            {
              name: t("ACC_SO_TIEN_GHI_CO"),
              attr: "CURRENCY_CREATE_AMOUNT",
              isCustomDataColumn: true,
              type: Constant.CREDIT,
            },
            {
              name: t("ACC_MO_TA"),
              attr: "CONTENT",
              notCenter: true,
            },
          ]}
          onPressRow={onPressRowTable}
          dataTable={dataV2Table}
          widthCell={!isDesktop ? 120 : undefined}
        />
      ) : (
        renderMobile
      )}
      {isShow && data.length === 0 ? (
        <EmptyBox title={t("ACC_KHONG_CO_GIAO_DICH_NAO")} />
      ) : isShow && data.length > 0 && dataV2Table.length === 0 ? (
        <EmptyBox title={t("COMMON_KHONG_TIM_THAY_TIM_KIEM")} />
      ) : (
        <></>
      )}
    </Block>
  );
}

export default StatementTable;
